import React, { useRef, useEffect, useMemo } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";

export const DEFAULT_CALENDAR_TOOLBAR = {
  left: "prev,next today",
  center: "title",
  right: "dayGridMonth,timeGridWeek,timeGridDay",
};

export const DEFAULT_CALENDAR_STYLE = {
  width: "100%",
  height: "65vh",
  backgroundColor: "#17296B",
  borderRadius: "8px",
  padding: "10px",
  color: "white",
  fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
};

export const DEFAULT_TODAY_STYLE = {
  backgroundColor: "#00b2ff",
  color: "white",
  borderRadius: "6px",
  padding: "3px",
};

export const DEFAULT_DAY_NUMBER_STYLE = {
  color: "white",
  fontWeight: "500",
};

const DEFAULT_HEADER_CELL_STYLE = {
  color: "white",
  textDecoration: "none",
  fontWeight: "600",
};

export const getDefaultEventBackgroundColor = (event) =>
  event.backgroundColor || event.color || "#0d6efd";

export const getEventTimeText = (event) => {
  if (event.allDay || !event.start) return "";

  const start = event.start;
  const hours = start.getHours().toString().padStart(2, "0");
  const minutes = start.getMinutes().toString().padStart(2, "0");

  return `${hours}:${minutes}`;
};

const defaultDayMatcher = (arg) => {
  const todayStr = new Date().toISOString().slice(0, 10);
  return arg.date.toISOString().slice(0, 10) === todayStr ? "today" : null;
};

const defaultDayStyles = {
  today: DEFAULT_TODAY_STYLE,
};

const CalendarView = ({
  events = [],
  onEventClick,
  onEventDrop,
  onSelect,
  initialView = "dayGridMonth",
  headerToolbar = DEFAULT_CALENDAR_TOOLBAR,
  availableViews = "dayGridMonth,timeGridWeek,timeGridDay",
  editable = true,
  selectable = true,
  fixedWeekCount = true,
  dayMaxEventRows = true,
  calendarStyle = {},
  todayStyle = DEFAULT_TODAY_STYLE,
  dayNumberStyle = DEFAULT_DAY_NUMBER_STYLE,
  headerCellStyle: headerCellStyleOverride = DEFAULT_HEADER_CELL_STYLE,
  dayTypeResolver = defaultDayMatcher,
  dayStyles = defaultDayStyles,
  eventColorResolver = getDefaultEventBackgroundColor,
  renderEventContent: customEventContentRenderer,
  eventDisplayStyle = {},
  fullCalendarProps = {},
}) => {
  const calendarRef = useRef(null);

  const resolvedHeaderToolbar = useMemo(() => ({
    ...headerToolbar,
    right: headerToolbar.right ?? availableViews,
  }), [availableViews, headerToolbar]);

  const headerCellStyle = useMemo(() => ({
    color: "white",
    textDecoration: "none",
    fontWeight: "600",
    ...headerCellStyleOverride,
  }), [headerCellStyleOverride]);

  const calendarContainerStyle = useMemo(() => ({
    ...DEFAULT_CALENDAR_STYLE,
    ...calendarStyle,
  }), [calendarStyle]);

  const resolvedDayStyles = useMemo(() => ({
    ...defaultDayStyles,
    today: todayStyle,
    ...dayStyles,
  }), [dayStyles, todayStyle]);

  useEffect(() => {
    const applyStyles = () => {
      document.querySelectorAll(".fc-button").forEach((btn) => {
        btn.style.backgroundColor = "#0d6efd";
        btn.style.border = "none";
        btn.style.color = "white";
        btn.style.fontWeight = "500";
        btn.style.margin = "0 4px";
        btn.style.borderRadius = "4px";
        btn.style.padding = "6px 12px";
        btn.style.cursor = "pointer";
      });

      document
        .querySelectorAll(".fc-col-header-cell .fc-col-header-cell-cushion")
        .forEach((el) => {
          el.style.color = headerCellStyle.color;
          el.style.textDecoration = headerCellStyle.textDecoration;
          el.style.fontWeight = headerCellStyle.fontWeight;
        });

      document.querySelectorAll(".fc-timegrid-body").forEach((el) => {
        el.style.height = "100%";
      });

      document.querySelectorAll(".fc-scroller-harness").forEach((el) => {
        el.style.height = "100%";
      });

      document.querySelectorAll(".fc-timegrid").forEach((el) => {
        el.style.height = "100%";
      });
    };

    applyStyles();

    const observer = new MutationObserver(applyStyles);
    const node = document.querySelector(".fc");
    if (node) {
      observer.observe(node, { childList: true, subtree: true });
    }

    return () => observer.disconnect();
  }, [events, headerCellStyle]);

  const applyDayStyle = (arg, style) => {
    if (!style) return;

    Object.entries(style).forEach(([key, value]) => {
      arg.el.style[key] = value;
    });
  };

  const dayCellDidMount = (arg) => {
    const dayType = dayTypeResolver?.(arg);
    applyDayStyle(arg, resolvedDayStyles[dayType]);

    if (dayType === "today" && !resolvedDayStyles.today) {
      applyDayStyle(arg, todayStyle);
    }

    const dayNumberEl = arg.el.querySelector(".fc-daygrid-day-number");
    if (dayNumberEl) {
      dayNumberEl.style.color = dayNumberStyle.color;
      dayNumberEl.style.fontWeight = dayNumberStyle.fontWeight;
    }
  };

  const renderCalendarEventContent = (eventInfo) => {
    if (customEventContentRenderer) {
      return customEventContentRenderer(eventInfo);
    }

    const { event } = eventInfo;
    const bgColor = eventColorResolver(event);
    const timeText = getEventTimeText(event);

    return (
      <div
        style={{
          backgroundColor: bgColor,
          padding: "4px 7px",
          width: "100%",
          borderRadius: "4px",
          color: "white",
          display: "flex",
          alignItems: "center",
          gap: "5px",
          fontSize: "0.90rem",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          ...eventDisplayStyle,
        }}
      >
        {timeText && (
          <span style={{ fontWeight: "bold", minWidth: "40px" }}>
            {timeText}
          </span>
        )}
        <span>{event.title}</span>
      </div>
    );
  };

  return (
    <div style={calendarContainerStyle}>
      <div style={{ flex: 1 }}>
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView={initialView}
          fixedWeekCount={fixedWeekCount}
          dayMaxEventRows={dayMaxEventRows}
          height="100%"
          contentHeight="100%"
          headerToolbar={resolvedHeaderToolbar}
          dayCellDidMount={dayCellDidMount}
          eventContent={renderCalendarEventContent}
          events={events}
          editable={editable}
          selectable={selectable}
          select={onSelect}
          eventClick={onEventClick}
          eventDrop={onEventDrop}
          {...fullCalendarProps}
        />
      </div>
    </div>
  );
};

export default CalendarView;
